<template>
  <div data-qa="alarm-actions" class="d-flex align-end justify-center">
    <v-tooltip
      :text="$t('editAlarmConfiguration')"
      location="top"
    >
      <template v-slot:activator="{ props }">
        <AnimatedButton v-bind="props" icon="edit"
         @click="openEdit($event, alarm.alarmId)" data-qa="edit-alarm"></AnimatedButton>
      </template>
    </v-tooltip>
    <v-tooltip :text="$t('deleteAlarmConfiguration')" location="top">
      <template v-slot:activator="{ props }">
        <AnimatedButton v-bind="props" icon="delete"
          @click="deleteAlarm" data-qa="delete-alarm"></AnimatedButton>
      </template>
    </v-tooltip>
    <v-tooltip :text="$t('cloneAlarmConfiguration')" location="top">
      <template v-slot:activator="{ props }">
        <AnimatedButton v-bind="props" icon="content_copy"
          @click="cloneAlarm" data-qa="clone-alarm"></AnimatedButton>
      </template>
    </v-tooltip>
    <v-tooltip :text="$t('table.counters')" location="top">
      <template v-slot:activator="{ props }">
        <AnimatedButton v-bind="props" icon="data_thresholding"
          @click="showDrawer($event, alarm.alarmId)" data-qa="data_thresholding"></AnimatedButton>
      </template>
    </v-tooltip>
  </div>
</template>

<script setup lang="ts">
import { IAlarm } from "../interfaces/IAlarm";
import AnimatedButton from "./UIComponents/AnimatedButton.vue";
import { modalSimpleStore } from "../stores/modal";
import ModalEnum from "../constant/ModalEnum";
import { dataStore } from "../stores/data";
import { useI18n } from "vue-i18n";

const dataStoreInstance = dataStore();

const { t } = useI18n();

const emit = defineEmits<{
  (e: 'refresh'): void;
}>();


const props = defineProps<{
  alarm: IAlarm;
}>();

const openEdit = async(event, alarmId) => {
  dataStoreInstance.editAlarmId = alarmId;
  await dataStoreInstance.openEdit(event);
}

const showDrawer = async(event, alarmId) => {
  dataStoreInstance.counterAlarmId = alarmId;
  await dataStoreInstance.openCounters(event);
};


const deleteAlarm = async() => {
  const alarmId = props.alarm.alarmId;
  modalSimpleStore.showModal({
    id: ModalEnum.CONFIRMED_MODAL,
    maxWidth: 500,
    title: t('deleteAlarmConfiguration'),
    message: t('configurationWillBeDeleted', {alarmName: props.alarm.name}),
    onSuccess: async () => {
      try {
        await dataStoreInstance.deleteConfigAlarm(alarmId);
      } catch (error) {
        modalSimpleStore.showModal({
          id: ModalEnum.ERROR_MODAL,
          title: "Error!",
          message: t("alarmDeletionFailed"),
          error: error,
        });
      } finally {
        modalSimpleStore.hideModal(ModalEnum.CONFIRMED_MODAL);
        modalSimpleStore.showModal({
          id: ModalEnum.SUCCESS_MODAL,
          title: "Success!",
          message: t("alarmConfigurationDeleted"),
        });
        emit("refresh");
        return;
      }
    },
    onClose: async () => {
      return modalSimpleStore.hideModal(ModalEnum.CONFIRMED_MODAL);
    },
  });
};

const cloneAlarm = async() => {
  const alarmId = props.alarm.alarmId;
  modalSimpleStore.showModal({
    id: ModalEnum.INPUT_MODAL,
    maxWidth: 500,
    title: t('cloneAlarmConfiguration'),
    message: t('configurationWillBeCloned', { alarmName: props.alarm.name }),
    onConfirmValidation: (input: string) => {
      if (!input || input.trim() === "") {
        return { valid: false, error: t("nameCannotBeEmpty") };
      } else {
        if (dataStoreInstance.getAlarms?.content) {
          for (const alarm of dataStoreInstance.getAlarms.content) {
            const { name } = alarm;
            if (input.trim() === name) {
              return { valid: false, error: t("alarmAlreadyExists", { alarmName: name }) };
            }
          }
        }
      }
      return { valid: true, error: "" };
    },
    onSuccess: async (input: string) => {
      try {
        await dataStoreInstance.cloneConfigAlarm(alarmId, {name: input});
      } catch (error) {
        modalSimpleStore.showModal({
          id: ModalEnum.ERROR_MODAL,
          title: "Error!",
          message: t("alarmCloningFailed"),
          error: error,
        });
      } finally {
        modalSimpleStore.hideModal(ModalEnum.INPUT_MODAL);
        modalSimpleStore.showModal({
          id: ModalEnum.SUCCESS_MODAL,
          title: "Success!",
          message: t("alarmConfigurationCloned"),
        });
        emit("refresh");
        return;
      }
    },
    onError: async (error) => {
      modalSimpleStore.showModal({
        id: ModalEnum.ERROR_MODAL,
        title: "Error!",
        message: error,
      });
    },
    onClose: async () => {
      return modalSimpleStore.hideModal(ModalEnum.INPUT_MODAL);
    },
  });
};
</script>

<style scoped>
</style>